import { APITypesV1, APITypesV2 } from "@cur8/api-client";
import {
  AnnotationURI,
  BookingResourceURI,
  FrameSequenceURI,
  ImmutableScanBlobURI,
  PatientBlobURI,
  PhysicalArtefactURI,
  PhysicalIdURI,
  RecordingBlobURI,
  RecordingURI,
  UserURI,
} from "@cur8/uri";
import { DateTime } from "luxon";
import {
  Annotation,
  AnnotationClassification,
  AnnotationData,
  BoundingBoxDataType,
  Box,
  LineDataType,
  NamedTimestampsDataType,
  Point,
  PointSpaceDataType,
  RangeDataType,
} from "../types/Annotation";
import {
  AggregatedBloodPressureData,
  AktiiaMonitorBloodPressureAssignmentData,
  Assignment,
  BloodPressureData,
} from "../types/Assignment";
import { AuditInformation, AuditRecord } from "../types/AuditInformation";
import {
  BookableSlot,
  BookingSlot,
  ScheduleViewSlot,
  ScheduleViewSlotPatientDetails,
  Slot,
} from "../types/Booking";
import { CheckinSessionState } from "../types/CheckinSessionState";
import { ImmutableScan } from "../types/ImmutableScan";
import { JournalNoteItem } from "../types/JournalNoteItem";
import { Dimension, Measurement } from "../types/Measurement";
import {
  ConsentData,
  ContactDetails,
  Patient,
  PersonName,
  Sex,
} from "../types/Patient";
import { Recording, Track } from "../types/Recording";
import { Room, RoomWithSheduleSlots } from "../types/Room";
import { SkinBookTracking } from "../types/SkinBookTracking";
import { UnknownURI } from "../types/URI";
import { Visit } from "../types/Visit";
import { VisitSummary } from "../types/VisitSummary";
import { Site } from "../types/Site";

function toBox(rect: APITypesV1.Rect): Box {
  return new Box(rect.left, rect.top, rect.width, rect.height);
}

function parseTargetURI(text: string): Annotation["targetURI"] {
  const uris = [
    FrameSequenceURI,
    RecordingBlobURI,
    RecordingURI,
    AnnotationURI,
    PatientBlobURI,
    PhysicalArtefactURI,
    ImmutableScanBlobURI,
  ];

  for (const URI of uris) {
    const uri = URI.parse(text);
    if (uri) {
      return uri;
    }
  }

  return UnknownURI.parse(text);
}

function toBoundingBoxData(
  source: APITypesV1.AnnotationData2D
): BoundingBoxDataType {
  return {
    rect: toBox(source.rect),
  };
}

function toPointSpaceData(
  source: APITypesV1.AnnotationData3D
): PointSpaceDataType {
  return {
    vertices: source.vertices as Point[],
    area: source.area ?? undefined,
  };
}

function toRangeData(source: APITypesV1.AnnotationDataRange): RangeDataType {
  return {
    range: source.range,
  };
}

function toLineData(source: APITypesV1.AnnotationDataLine2D): LineDataType {
  return {
    line: source.line,
  };
}

function toNamedTimestampsData(
  source: APITypesV1.AnnotationDataNamedTimestamps
): NamedTimestampsDataType {
  return {
    namedTimestamps: source.namedTimestamps,
  };
}

function toAnnotationData(
  source: APITypesV1.Annotation["data"]
): AnnotationData {
  if (source === null) {
    return undefined;
  }

  if ("line" in source) {
    return toLineData(source);
  }

  if ("namedTimestamps" in source) {
    return toNamedTimestampsData(source);
  }

  if ("range" in source) {
    return toRangeData(source);
  }

  if ("rect" in source) {
    return toBoundingBoxData(source);
  }

  if ("vertices" in source) {
    return toPointSpaceData(source);
  }

  return undefined;
}

function toBloodPressure(
  source: APITypesV1.AggregatedBloodPressureData
): AggregatedBloodPressureData {
  return {
    avgDay: source.avgDay,
    avgNight: source.avgNight,
    maxDay: source.maxDay,
    maxNight: source.maxNight,
    measuredAt: source.measuredAt
      ? DateTime.fromISO(source.measuredAt)
      : undefined,
    minDay: source.minDay,
    minNight: source.minNight,
  };
}

function toRawBloodPressureData(
  source: APITypesV1.BloodPressureData
): BloodPressureData {
  return {
    bloodPressure: source.bloodPressure,
    measuredAt: source.measuredAt
      ? DateTime.fromISO(source.measuredAt)
      : undefined,
  };
}

function toAktiiaAssignmentData(
  source: APITypesV1.AktiiaMonitorBloodPressureAssignment
): AktiiaMonitorBloodPressureAssignmentData {
  return {
    $type: "AktiiaMonitorBloodPressureAssignment",
    deviceSerialNumber: source.deviceSerialNumber,
    measurements: source.measurements.map(toBloodPressure),
    minCompletedMeasurements: source.minCompletedMeasurements,
    rawMeasurements: source.rawMeasurements.map(toRawBloodPressureData),
    returnAt: DateTime.fromISO(source.returnAt),
    returnLocation: source.returnLocation,
    doctorsNote: source.doctorsNote ?? undefined,
    returnedAt: source.returnedAt
      ? DateTime.fromISO(source.returnedAt)
      : undefined,
    wearableLocation: source.wearableLocation,
    state: source.state,
  };
}

function toAssignmentData(source: APITypesV1.Assignment["data"]) {
  if (source.$type === "AktiiaMonitorBloodPressureAssignment") {
    return toAktiiaAssignmentData(
      source as APITypesV1.AktiiaMonitorBloodPressureAssignment
    );
  }

  return source;
}

export function toAssignment(source: APITypesV1.Assignment): Assignment {
  return {
    id: source.assignmentId,
    assignedTo: source.assignedTo
      ? UserURI.fromString(source.assignedTo)
      : undefined,
    eTag: source.eTag ?? undefined,
    allowedActions: source.allowedActions,
    finishAt: DateTime.fromISO(source.finishAt),
    startAt: DateTime.fromISO(source.startAt),
    patientId: source.patientId,
    audit: toAuditInformation(source.audit),
    data: toAssignmentData(source.data),
  };
}

export function toAnnotation(source: APITypesV1.Annotation): Annotation {
  return {
    id: source.id,
    uri: new AnnotationURI(source.patientId, source.id),
    patientId: source.patientId,
    physicalArtefactId: source.physicalArtefactId ?? undefined,
    targetURI: parseTargetURI(source.targetUri),
    acceptState: source.acceptState,
    applicationSpecificTarget: source.applicationSpecificTarget ?? undefined,
    data: toAnnotationData(source.data),
    classification: source.classification as AnnotationClassification,
    comment: source.comment ?? undefined,
    createdAt: DateTime.fromISO(source.createdAt),
    updatedAt: DateTime.fromISO(source.updatedAt),
    audit: toAuditInformation(source.audit),
  };
}

export function toBookingSlot(source: APITypesV1.Slot): BookingSlot {
  return {
    id: source.slotId,
    eTag: source.eTag,
    isFree: source.isFree,
    isBooked: source.isBooked,
    isReserved: source.isReserved,
    checkedIn: source.checkedIn,
    patientId: source.patientId ?? undefined,
    startTime: DateTime.fromISO(source.startTime),
    endTime: DateTime.fromISO(source.endTime),
  };
}

export function toBookableSlot(source: APITypesV2.BookableSlot): BookableSlot {
  return {
    slotId: source.slotId,
    siteId: source.siteId,
    siteName: source.siteName,
    startTime: DateTime.fromISO(source.startTime),
    endTime: DateTime.fromISO(source.endTime),
    eTag: source.eTag,
  };
}

export function toRoomWithSheduleViewSlot(
  source: APITypesV2.RoomWithSlotsV2
): RoomWithSheduleSlots {
  return {
    id: source.roomId,
    displayName: source.displayName ?? undefined,
    site: source.site ? toSite(source.site) : undefined,
    slots: source.slots ? source.slots.map(toScheduleViewSlot) : undefined,
  };
}

export function toScheduleViewSlot(
  source: APITypesV2.ScheduleViewSlotV2
): ScheduleViewSlot {
  return {
    roomId: source.roomId,
    id: source.slotId,
    isFree: source.isFree,
    isBooked: source.isBooked,
    isReserved: !source.isOpenPublicly,
    isOpenPublicly: source.isOpenPublicly,
    checkedIn: source.checkedIn,
    patientDetails: source.patientDetails
      ? toScheduleViewSlotPatientDetails(source.patientDetails)
      : undefined,
    patientId: source.patientDetails?.patientId ?? undefined,
    startTime: DateTime.fromISO(source.startTime),
    endTime: DateTime.fromISO(source.endTime),
    eTag: source.eTag,
  };
}

export function toScheduleViewSlotPatientDetails(
  source: APITypesV1.ScheduleViewSlotPatientDetails
): ScheduleViewSlotPatientDetails {
  return {
    patientId: source.patientId,
    displayName: source.displayName ?? undefined,
    finishedOnboarding: source.finishedOnboarding,
  };
}

export function toTrack(source: APITypesV1.RecordingTrack): Track {
  return {
    id: source.id,
    deviceId: source.deviceId,
    recordingId: source.recordingId,
    createdAt: DateTime.fromISO(source.createdAt),
    contentType: source.contentType,
    codecType: source.codecType,
    nodeName: source.nodeName ?? undefined,
    publisherName: source.publisherName ?? undefined,
  };
}

export function toRecording(
  source: APITypesV1.RecordingSummary | APITypesV1.Recording
): Recording {
  return {
    id: source.id,
    uri: new RecordingURI(source.deviceId, source.id),
    patientId: source.patientId ?? undefined,
    patientInformation: source.patientInformation,
    deviceId: source.deviceId,
    recordingType: source.recordingType,
    metadata: source.metadata || {},
    createdAt: DateTime.fromISO(source.createdAt),
    closedAt: DateTime.fromISO(source.closedAt),
  };
}

function toPersonName(source: APITypesV1.PersonName): PersonName {
  return {
    ...source,
    displayName: source.displayName ?? undefined,
  };
}

function toPhysicalId(source: string) {
  return PhysicalIdURI.parse(source) || source;
}

function toSourceURI(source: string) {
  return RecordingURI.parse(source) || UnknownURI.parse(source);
}

function toContactDetails(source?: APITypesV1.ContactDetails): ContactDetails {
  return {
    addressLines: source?.addressLines || [],
    countryOrRegion: source?.countryOrRegion || "",
    zipCode: source?.zipCode || "",
    phoneNumbers: source?.phoneNumbers || [],
    email: source?.email || "",
  };
}

function toConsentData(source?: APITypesV1.ConsentData): ConsentData {
  return {
    createdAt: source?.createdAt
      ? DateTime.fromISO(source?.createdAt)
      : undefined,
    expiresAt: source?.expiresAt
      ? DateTime.fromISO(source?.expiresAt)
      : undefined,
  };
}

function toSex(source?: APITypesV1.Sex): Sex {
  if (source === APITypesV1.Sex.Male) {
    return Sex.Male;
  }
  if (source === APITypesV1.Sex.Female) {
    return Sex.Female;
  }
  return Sex.Unknown;
}

function toDimension(source: APITypesV1.Dimension): Dimension {
  return {
    name: source.name,
    value: source.value,
    unit: source.unit ?? null,
  };
}

export function toPatientDetails(source: APITypesV1.PatientDetails): Patient {
  return {
    patientId: source.externalId,
    physicalIds: source.physicalIds.map(toPhysicalId),
    name: source.name ? toPersonName(source.name) : undefined,
    contactDetails: toContactDetails(source.contactDetails),
    dateOfBirth: source.dateOfBirth
      ? DateTime.fromFormat(source.dateOfBirth, "yyyy-LL-dd")
      : undefined,
    consent: toConsentData(source.consent),
    sex: toSex(source.sex),
    allergies: source.allergies || [],
    preferredLanguage: source.preferredLanguage ?? undefined,
    ethnicities: source.ethnicities || [],
    groupIds: source.groupIds,
    primaryConsentRuleset: source.primaryConsentRuleset,
    flags: source.flags || [],
  };
}

export function toImmutableScan(
  source: APITypesV1.ImmutableScan
): ImmutableScan {
  return {
    id: source.id,
    version: source.version,
    patientId: source.patientId,
    sourceUris: source.sourceUris.map(toSourceURI),
    versionCreatedAt: DateTime.fromISO(source.versionCreatedAt),
    timestamp: DateTime.fromISO(source.timestamp),
    state: source.state,
    scanType: source.type,
    resultStateSummary: source.resultStateSummary,
    journalNoteId: source.journalNoteId ?? undefined,
    visitId: source.visitId,
  };
}

export function toMeasurement(source: APITypesV1.Measurement): Measurement {
  return {
    id: source.id,
    sourceUri: source.sourceUri,
    generator: source.generator ?? undefined,
    timestampStart: DateTime.fromISO(source.timestampStart),
    timestampEnd: source.timestampEnd
      ? DateTime.fromISO(source.timestampEnd)
      : undefined,
    dimensions: source.dimensions.map((dimension) => toDimension(dimension)),
    metricName: source.metricName,
  };
}

export function toAuditInformation(
  source: APITypesV1.AuditInformation
): AuditInformation {
  return {
    created: source.created ? toAuditRecord(source.created) : undefined,
    lastModified: source.lastModified
      ? toAuditRecord(source.lastModified)
      : undefined,
  };
}

export function toAuditRecord(source: APITypesV1.AuditRecord): AuditRecord {
  return {
    userUri: source.userUri ? UserURI.fromString(source.userUri) : undefined,
    timeStamp: source.timeStamp
      ? DateTime.fromISO(source.timeStamp)
      : undefined,
  };
}

export function toCheckinSessionState(
  source: APITypesV1.CheckinSessionState
): CheckinSessionState {
  return {
    sessionId: source.sessionId,
    sessionState: source.sessionState,
    conciergeState: source.conciergeState ?? undefined,
    patientId: source.patientId ?? undefined,
    patientState: source.patientState ?? undefined,
    auditInformation: toAuditInformation(source.auditInformation),
    eTag: source.eTag ?? undefined,
    expiresAt: DateTime.fromISO(source.expiresAt ?? ""),
  };
}

export function toRoom(source: APITypesV1.Room | APITypesV2.Room): Room {
  return {
    id: source.id,
    displayName: source.displayName ?? undefined,
    patientGroupId: source.patientGroupId ?? undefined,
    site: source.site ? toSite(source.site) : undefined,
    supportedVisitTypes: source.supportedVisitTypes ?? undefined,
  };
}

export function toSite(source: APITypesV2.Site): Site {
  return source as Site;
}

export function toSlot(source: APITypesV2.SlotV2): Slot {
  return {
    slotId: source.slotId,
    room: source.room ? toRoom(source.room) : undefined,
    patientId: source.patientId ?? undefined,
    startTime: DateTime.fromISO(source.startTime),
    endTime: DateTime.fromISO(source.endTime),
    bookingTokenId: source.bookingTokenId ?? undefined,
    isOpenPublicly: source.isOpenPublicly,
    isFree: source.isFree,
    visitTypeId: source.visitTypeId,
    eTag: source.eTag,
  };
}

export function toVisit(source: APITypesV1.Visit): Visit {
  return {
    visitId: source.visitId,
    patientId: source.patientId,
    slotId: source.slotId,
    status: source.status,
    roomId: source.roomId ?? undefined,
    checkinStartTimestamp: source.checkinStartTimestamp
      ? DateTime.fromISO(source.checkinStartTimestamp)
      : undefined,
    checkinEndTimestamp: source.checkinEndTimestamp
      ? DateTime.fromISO(source.checkinEndTimestamp)
      : undefined,
    immutableScansPerformed: source.immutableScansPerformed,
    eTag: source.eTag ?? undefined,
  };
}

export function toVisitSummary(source: APITypesV1.VisitSummary): VisitSummary {
  return {
    id: source.id,
    patientId: source.patientId,
    visitDate: DateTime.fromISO(source.visitDate),
    followUpDate: source.followUpDate
      ? DateTime.fromISO(source.followUpDate)
      : undefined,
    summaryText: source.summaryText,
    audit: toAuditInformation(source.audit),
  };
}

export function toJournalNoteItem(
  source: APITypesV1.JournalNote
): JournalNoteItem {
  return {
    journalItemId: source.journalItemId,
    patientId: source.patientId,
    visitId: source.visitId ?? undefined,
    journalVersionId: source.version,
    journalNoteType: source.journalNoteType,
    note: source.note,
    eTag: source.eTag ?? undefined,
    auditInformation: toAuditInformation(source.audit),
  };
}

export function toSkinBookTracking(
  source: APITypesV1.SkinBookTracking
): SkinBookTracking {
  return {
    patientId: source.patientId,
    patientDisplayName: source.patientDisplayName,
    patientPhoneNumber: source.patientPhoneNumber,
    scanId: source.scanId,
    sourceUris: source.sourceUris.map(toSourceURI),
    taskGroup: source.taskGroup,
    scanCreatedAt: source.scanCreatedAt
      ? DateTime.fromISO(source.scanCreatedAt)
      : undefined,
    skinBookStatus: source.skinBookStatus,
    appointmentStartTime: source.appointmentStartTime
      ? DateTime.fromISO(source.appointmentStartTime)
      : undefined,
    visitId: source.visitId ?? undefined,
    skinAppointmentStatus: source.skinAppointmentStatus,
    nothingSMSNotificationId: source.nothingSMSNotificationId ?? undefined,
    nothingSMSNotificationScheduledTimestamp:
      source.nothingSMSNotificationScheduledTimestamp
        ? DateTime.fromISO(source.nothingSMSNotificationScheduledTimestamp)
        : undefined,
    eTag: source.eTag ?? undefined,
    audit: toAuditInformation(source.audit),
  };
}
